import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Statistic } from 'antd';
import {
  DollarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CalculatorOutlined,
} from '@ant-design/icons';
import { PaymentStatisticsResponse } from '../../model/Payment.model';
import PaymentService from '../../service/payment-service/Payment.service';
import dayjs, { Dayjs } from 'dayjs';

interface PaymentStatisticsProps {
  selectedDate: Dayjs;
  employeeId?: number;
  isAdmin: boolean;
}

const PaymentStatistics: React.FC<PaymentStatisticsProps> = ({
  selectedDate,
  employeeId,
  isAdmin,
}) => {
  const [loading, setLoading] = useState(false);
  const [statisticsData, setStatisticsData] =
    useState<PaymentStatisticsResponse>({
      totalPayments: 0,
      completedPayments: 0,
      pendingPayments: 0,
      totalAmount: 0,
    });

  const loadStatistics = useCallback(async () => {
    if (!selectedDate || (!isAdmin && !employeeId)) return;

    try {
      setLoading(true);
      const year = selectedDate.year();
      const month = selectedDate.month() + 1;
      const day = selectedDate.date();

      const response = isAdmin
        ? await PaymentService.getPaymentStatisticsByDate(year, month, day)
        : await PaymentService.getPaymentStatisticsByDateAndEmployee(
            year,
            month,
            day,
            employeeId!
          );

      if (response.success && response.data) {
        setStatisticsData(response.data);
      } else {
        setStatisticsData({
          totalPayments: 0,
          completedPayments: 0,
          pendingPayments: 0,
          totalAmount: 0,
        });
      }
    } catch (error) {
      console.error('Error loading payment statistics:', error);
      setStatisticsData({
        totalPayments: 0,
        completedPayments: 0,
        pendingPayments: 0,
        totalAmount: 0,
      });
    } finally {
      setLoading(false);
    }
  }, [selectedDate, employeeId, isAdmin]);

  useEffect(() => {
    loadStatistics();
  }, [loadStatistics]);

  // Sử dụng dữ liệu từ API
  const { totalPayments, completedPayments, pendingPayments, totalAmount } =
    statisticsData;

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(amount);
  };

  const statisticsItems = [
    {
      title: 'Tổng thanh toán',
      value: totalPayments,
      icon: <CalculatorOutlined className="text-blue-500" />,
      color: '#2563eb', // blue-600
      bgColor: 'bg-blue-50',
    },
    {
      title: 'Đã hoàn thành',
      value: completedPayments,
      icon: <CheckCircleOutlined className="text-green-500" />,
      color: '#16a34a', // green-600
      bgColor: 'bg-green-50',
    },
    {
      title: 'Chờ xử lý',
      value: pendingPayments,
      icon: <ClockCircleOutlined className="text-orange-500" />,
      color: '#ea580c', // orange-600
      bgColor: 'bg-orange-50',
    },
    {
      title: 'Tổng giá trị',
      value: formatCurrency(totalAmount),
      icon: <DollarOutlined className="text-purple-500" />,
      color: '#9333ea', // purple-600
      bgColor: 'bg-purple-50',
    },
  ];

  return (
    <Row gutter={[16, 16]}>
      {statisticsItems.map((stat, index) => (
        <Col xs={24} sm={12} lg={6} key={index}>
          <Card
            loading={loading}
            className={`${stat.bgColor} border-0 shadow-sm hover:shadow-md transition-shadow duration-300`}
          >
            <Statistic
              title={
                <span className="text-gray-600 font-medium">{stat.title}</span>
              }
              value={stat.value}
              valueStyle={{
                color: stat.color,
                fontSize: '24px',
                fontWeight: '600',
              }}
              prefix={<div className="text-2xl mb-1">{stat.icon}</div>}
            />
          </Card>
        </Col>
      ))}
    </Row>
  );
};

export default PaymentStatistics;
